<template>
  <v-container fluid class="dashboard-page">
    <!-- Skeleton loader while waiting for API -->
    <div v-if="!services" class="dashboard-skeleton">
      <!-- Header skeleton -->
      <div class="dskel-header">
        <div class="dskel-line dskel-line--xl"></div>
        <div class="dskel-line dskel-line--md"></div>
      </div>

      <!-- Banner skeleton -->
      <div class="dskel-banner"></div>

      <!-- Stats bar skeleton -->
      <div class="dskel-stats">
        <div class="dskel-stats__item">
          <div class="dskel-circle"></div>
          <div class="dskel-line dskel-line--sm"></div>
          <div class="dskel-line dskel-line--xs"></div>
        </div>
        <div class="dskel-stats__divider"></div>
        <div class="dskel-stats__item">
          <div class="dskel-circle"></div>
          <div class="dskel-line dskel-line--md"></div>
        </div>
        <div class="dskel-stats__divider"></div>
        <div class="dskel-stats__item">
          <div class="dskel-circle"></div>
          <div class="dskel-line dskel-line--sm"></div>
        </div>
      </div>

      <!-- Service cards skeleton -->
      <div class="dskel-grid">
        <div class="dskel-card" v-for="n in 6" :key="n">
          <div class="dskel-card__accent"></div>
          <div class="dskel-card__body">
            <div class="dskel-card__top">
              <div class="dskel-card__icon"></div>
              <div class="dskel-card__lines">
                <div class="dskel-line dskel-line--md"></div>
                <div class="dskel-line dskel-line--lg"></div>
              </div>
            </div>
            <div class="dskel-card__bottom">
              <div class="dskel-line dskel-line--xs"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else>
      <!-- Welcome Header -->
      <div class="dashboard-header">
        <h1 class="dashboard-header__title">
          Welcome back, <span class="dashboard-header__name">{{ auth.name || 'User' }}</span>
        </h1>
        <p class="dashboard-header__subtitle">
          Select a service module to get started
        </p>
      </div>

      <!-- Banner Row: Greeting + Stats side by side -->
      <div class="banner-row">
        <!-- Greeting Banner -->
        <div class="greeting-banner">
          <div class="greeting-banner__glow"></div>
          <div class="greeting-banner__left">
            <div class="greeting-banner__emoji">{{ greetingEmoji }}</div>
            <div>
              <h2 class="greeting-banner__text">{{ greetingText }}</h2>
              <p class="greeting-banner__tip">"{{ dailyTip }}"</p>
            </div>
          </div>
          <div class="greeting-banner__mascot" @click="nextBuddy()">
            <transition name="bubble-pop">
              <div v-if="buddySpeech" :key="buddySpeech" class="buddy-bubble">
                {{ buddySpeech }}
              </div>
            </transition>
            <transition name="mascot-fade" mode="out-in">
              <img
                :key="currentBuddyIndex"
                :src="buddyImages[currentBuddyIndex]"
                alt="Tax Buddy"
                class="greeting-banner__mascot-img"
              />
            </transition>
          </div>
        </div>

        <!-- Info Bar -->
        <div class="stats-bar">
        <!-- Weather -->
        <div class="stats-bar__section stats-bar__section--weather" v-if="weather.temp !== null">
          <div class="stats-bar__item">
            <div class="stats-bar__icon-circle stats-bar__icon-circle--amber">
              <v-icon size="18" color="#f59e0b">{{ weatherIcon }}</v-icon>
            </div>
            <span class="stats-bar__value">{{ weather.temp }}°C</span>
            <span class="stats-bar__label">{{ weather.desc }}</span>
            <span class="stats-bar__location" v-if="weather.location">
              <v-icon size="12" color="var(--app-text-faint)">mdi-map-marker</v-icon>
              {{ weather.location }}
            </span>
            <span class="stats-bar__location-skel" v-else-if="weather.location === null && weather.temp !== null">
              <v-icon size="12" color="#cbd5e1">mdi-map-marker</v-icon>
              <span class="skel-text"></span>
            </span>
          </div>
        </div>
        <div class="stats-bar__section stats-bar__section--weather" v-else>
          <div class="stats-bar__item">
            <div class="stats-bar__icon-circle stats-bar__icon-circle--gray">
              <v-icon size="18" color="#cbd5e1">mdi-weather-partly-cloudy</v-icon>
            </div>
            <span class="skel-text skel-text--temp"></span>
            <span class="skel-text skel-text--desc"></span>
          </div>
        </div>

        <!-- Date -->
        <div class="stats-bar__section stats-bar__section--date">
          <div class="stats-bar__item">
            <div class="stats-bar__icon-circle stats-bar__icon-circle--slate">
              <v-icon size="16" color="var(--app-text-muted)">mdi-calendar-outline</v-icon>
            </div>
            <span class="stats-bar__value">{{ currentDate }}</span>
          </div>
        </div>

        <!-- Live Clock -->
        <div class="stats-bar__section stats-bar__section--clock">
          <div class="stats-bar__item">
            <div class="stats-bar__icon-circle stats-bar__icon-circle--blue">
              <v-icon size="16" color="var(--app-accent)">mdi-clock-outline</v-icon>
            </div>
            <div class="clock">
              <span class="clock__digit">{{ clockHours }}</span>
              <span class="clock__colon" :class="{ 'clock__colon--blink': clockBlink }">:</span>
              <span class="clock__digit">{{ clockMinutes }}</span>
              <span class="clock__colon" :class="{ 'clock__colon--blink': clockBlink }">:</span>
              <span class="clock__digit">{{ clockSeconds }}</span>
              <span class="clock__period">{{ clockPeriod }}</span>
            </div>
          </div>
        </div>
        </div>
      </div>

      <!-- Zoned Service Cards -->
      <template v-for="(zone, zIndex) of serviceZones">
        <div
          v-if="zone.items.length"
          :key="'zone-' + zIndex"
          class="service-zone"
        >
          <!-- Zone header -->
          <div class="service-zone__header">
            <div class="service-zone__heading">
              <h2 class="service-zone__title">{{ zone.title }}</h2>
              <p class="service-zone__subtitle">{{ zone.subtitle }}</p>
            </div>
            <span class="service-zone__rule"></span>
            <button
              v-if="zone.id === 'event'"
              class="running-events-pill"
              @click="$refs.runningEvents.open()"
            >
              <span class="running-events-pill__dot"></span>
              <v-icon size="15" color="#0284c7" class="running-events-pill__icon">mdi-calendar-clock</v-icon>
              <span class="running-events-pill__label">
                {{ runningEventsCount }} Active
              </span>
              <v-icon size="15" color="#0284c7" class="zone-pill__chevron">mdi-chevron-right</v-icon>
            </button>
            <router-link
              v-if="zone.id === 'event' && isHrdcSuperAdminUser"
              :to="{
                name: 'PageServiceDashboard',
                params: { serviceKey: 'hrdc_event_settings' },
              }"
              class="event-settings-pill"
            >
              <v-icon size="15" color="#64748b">mdi-cog-outline</v-icon>
              <span class="event-settings-pill__label">Event Settings</span>
              <v-icon size="15" color="#64748b" class="zone-pill__chevron">mdi-chevron-right</v-icon>
            </router-link>
          </div>

          <!-- Zone cards -->
          <div class="services-grid">
            <router-link
              v-for="entry of zone.items"
              :key="entry.index"
              :to="entry.service.isEnabled ? {
                name: 'PageServiceDashboard',
                params: { serviceKey: entry.service.key },
              } : ''"
              :event="entry.service.isEnabled ? 'click' : ''"
              class="service-card-link"
            >
              <div
                class="service-card"
                :class="{
                  'service-card--disabled': !entry.service.isEnabled && entry.service.isAllowed,
                }"
              >

                <div class="service-card__content">
                  <!-- Top row: icon + title + status -->
                  <div class="service-card__top">
                    <div
                      class="service-card__icon"
                      :style="{
                        color: getServiceAccent(entry.index, entry.service.key),
                      }"
                    >
                      <v-icon :color="getServiceAccent(entry.index, entry.service.key)" size="22">{{ getServiceIcon(entry.service.key) }}</v-icon>
                    </div>
                    <div class="service-card__info">
                      <h3 class="service-card__name">{{ entry.service.name }}</h3>
                    </div>
                  </div>

                  <!-- Bottom row: status.
                       Only the NEGATIVE state is labelled. "Active" was true of
                       every card in the grid at once — inactive event modules
                       are filtered out before they get here (visibleServices) —
                       so it carried no information while being the loudest
                       thing on the card. "Inactive" is rare and worth saying. -->
                  <div class="service-card__bottom">
                    <span
                      v-if="!entry.service.isEnabled"
                      class="service-card__status service-card__status--disabled"
                    >
                      Inactive
                    </span>

                    <v-icon
                      v-if="entry.service.isEnabled && entry.service.isAllowed"
                      size="16"
                      class="service-card__go"
                    >mdi-arrow-right</v-icon>
                  </div>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </template>
    </div>

    <!-- Currently Running Events dialog -->
    <DashboardRunningEventsDialog
      ref="runningEvents"
      @update:count="runningEventsCount = $event"
    />
  </v-container>
</template>

<script>
import Api from "@/objects/api";
import Breadcrumb from "@/objects/breadcrumb";
import { mapState } from "vuex";
import DashboardRunningEventsDialog from "@/views/console/DashboardRunningEventsDialog.vue";
import { themeState } from "@/plugins/theme";
import { darkAccent } from "@/objects/accentColor";

export default {
  components: {
    DashboardRunningEventsDialog,
  },
  computed: {
    // Reactive global theme flag — getServiceAccent() reads it during render,
    // so flipping the navbar toggle re-tints the icon tiles immediately.
    isDark() {
      return themeState.dark;
    },
    ...mapState({
      debugMode: (state) => state.debugMode.data,
      auth: (state) => state.auth.data,
      rolePermission: (state) => state.rolePermission.data,
      services: (state) => state.service.data.services,
    }),
    // Hide services the user has no access to (no "Restricted" cards).
    // Additionally lock "hrdc_event_settings" to HRDC Super Admin only,
    // matching the navbar gate in LayoutConsoleV2.
    isHrdcSuperAdminUser() {
      const slugs = this.$_.map((this.auth && this.auth.roles) || [], "slug");
      return slugs.includes("hrdc-super-admin");
    },
    visibleServices() {
      const list = (this.services || []).filter((s) => {
        if (!s || !s.isAllowed) return false;
        const isEventModule =
          s.key &&
          s.key.startsWith("hrdc_") &&
          s.key !== "hrdc_event_settings";
        if (isEventModule && s.isEnabled === false) return false;
        return true;
      });
      if (this.isHrdcSuperAdminUser) return list;
      return list.filter((s) => s.key !== "hrdc_event_settings");
    },
    // Split the visible services into 3 clear-cut zones for the dashboard.
    // Zone 1: HRDC taxPOD only  |  Zone 2: HRDC event modules
    // Zone 3: Event Settings, SSO and anything else.
    serviceZones() {
      const { getEventConfig } = require("@/services/hrdc_event/eventRegistry");
      const zone1 = [];
      const zone2 = [];
      const zone3 = [];
      this.visibleServices.forEach((service, index) => {
        const entry = { service, index };
        const key = service.key || "";
        if (key === "hrdc") {
          zone1.push(entry);
        } else if (key !== "hrdc_event_settings" && getEventConfig(key)) {
          zone2.push(entry);
        } else {
          zone3.push(entry);
        }
      });
      return [
        {
          id: "taxpod",
          title: "HRDC taxPOD",
          subtitle: "Core taxPOD service",
          color: "#0ea5e9",
          items: zone1,
        },
        {
          id: "event",
          title: "HRDC Event",
          subtitle: "Event application modules",
          color: "#8b5cf6",
          items: zone2,
        },
        {
          id: "others",
          title: "Others",
          subtitle: "Configuration and access management",
          color: "#10b981",
          items: zone3,
        },
      ];
    },
    greetingText() {
      const h = new Date().getHours();
      if (h < 12) return 'Good Morning!';
      if (h < 17) return 'Good Afternoon!';
      return 'Good Evening!';
    },
    greetingEmoji() {
      const h = new Date().getHours();
      if (h < 12) return '🌤️';
      if (h < 17) return '☀️';
      return '🌙';
    },
    dailyTip() {
      const tips = [
        'Stay organized — check your pending tasks first.',
        'A quick review keeps things on track.',
        'Great things happen one step at a time.',
        'Focus on what matters most today.',
        'Small progress is still progress.',
        'Take a moment to prioritize your day.',
        'Consistency beats intensity. Keep going!',
        'Done is better than perfect.',
        'One task at a time, one win at a time.',
        'Clear inbox, clear mind.',
        'Start with the hardest task — the rest will feel easy.',
        'Review yesterday, plan today, prepare tomorrow.',
        'Your future self will thank you for today\'s effort.',
        'Discipline is choosing what you want most over what you want now.',
        'Every expert was once a beginner.',
        'Productivity is never an accident — it\'s a result of commitment.',
        'Don\'t count the days, make the days count.',
        'Success is the sum of small efforts repeated daily.',
        'What gets measured gets managed.',
        'The secret of getting ahead is getting started.',
        'It always seems impossible until it\'s done.',
        'Action is the foundational key to all success.',
        'Quality means doing it right when no one is looking.',
        'Simplicity is the ultimate sophistication.',
        'Work smarter, not harder.',
        'Progress, not perfection.',
        'A goal without a plan is just a wish.',
        'Your time is limited — don\'t waste it.',
        'Be the energy you want to attract.',
        'The best time to start is now.',
        'Efficiency is doing things right; effectiveness is doing the right things.',
      ];
      // Hash the date string to pick a tip — different each day, consistent within the day
      const dateStr = new Date().toISOString().slice(0, 10);
      let hash = 0;
      for (let i = 0; i < dateStr.length; i++) {
        hash = ((hash << 5) - hash) + dateStr.charCodeAt(i);
        hash |= 0;
      }
      return tips[Math.abs(hash) % tips.length];
    },
    currentDate() {
      const d = new Date();
      const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
    },
    weatherIcon() {
      const desc = (this.weather.desc || '').toLowerCase();
      if (desc.includes('rain') || desc.includes('drizzle')) return 'mdi-weather-rainy';
      if (desc.includes('thunder') || desc.includes('storm')) return 'mdi-weather-lightning-rainy';
      if (desc.includes('cloud') || desc.includes('overcast')) return 'mdi-weather-cloudy';
      if (desc.includes('partly') || desc.includes('scattered')) return 'mdi-weather-partly-cloudy';
      if (desc.includes('clear') || desc.includes('sunny')) return 'mdi-weather-sunny';
      if (desc.includes('haze') || desc.includes('mist') || desc.includes('fog')) return 'mdi-weather-fog';
      return 'mdi-weather-partly-cloudy';
    },
  },
  data: () => ({
    apiPermission: new Api(),
    apiTeam: new Api(),
    breadcrumb: new Breadcrumb(),
    runningEventsCount: 0,
    clockHours: '00',
    clockMinutes: '00',
    clockSeconds: '00',
    clockPeriod: 'AM',
    clockBlink: true,
    clockInterval: null,
    buddyImages: [
      '/images/tax-buddy.png',
      '/images/tax-buddy-2.png',
      '/images/tax-buddy-3.png',
    ],
    shuffledBuddies: [
      '/images/tax-buddy.png',
      '/images/tax-buddy-2.png',
      '/images/tax-buddy-3.png',
    ],
    currentBuddyIndex: 0,
    buddyInterval: null,
    buddySpeech: null,
    buddySpeechTimeout: null,
    buddyDialogs: [
      "Yeah! 🎉",
      "Woohoo! 🙌",
      "Ouch! 😵",
      "Yiii~ 😆",
      "Haha! 😂",
      "Hello! 👋",
      "Hey there! 😊",
      "Weeee! 🎢",
      "Oof! 😅",
      "Yay! ⭐",
      "Whoa! 😮",
      "Hmm? 🤔",
      "Tada! 🎩",
      "Boop! 👆",
      "Hehe~ 🤭",
      "Hi hi! 💙",
      "Oh wow! 🌟",
      "Eeek! 🙈",
      "Let's go! 🚀",
      "Nice! 👍",
      "Aiyo~ 😜",
      "Wahh! 😲",
      "Sup! ✌️",
      "Yo! 😎",
      "Teehee~ 🤗",
      "Ayayay! 💫",
      "Woot! 🎊",
      "Bravo! 👏",
      "Ehehe~ 😋",
      "Pow! 💥",
    ],
    weather: {
      temp: null,
      desc: null,
      location: null,
    },
    accentColors: [
      '#0ea5e9',
      '#8b5cf6',
      '#f59e0b',
      '#10b981',
      '#ef4444',
      '#ec4899',
      '#6366f1',
      '#14b8a6',
      '#f97316',
      '#06b6d4',
    ],
    serviceIconMap: {
      hrdc: 'mdi-file-certificate-outline',
      hrdc_einvoicing: 'mdi-receipt-text-outline',
      hrdc_tax_summit: 'mdi-podium',
      hrdc_tpdiy: 'mdi-tools',
      hrdc_stamp_duty: 'mdi-stamper',
      sso: 'mdi-account-group-outline',
    },
  }),
  created() {
    this.breadcrumb.add({
      text: "Dashboard",
      to: { name: "PageConsoleDashboard" },
      exact: true,
    });

    var internalRoleSlugs = this.$_.map(this.auth.roles, "slug");
    this.apiPermission.setMethod(`POST`);
    this.apiPermission.setUrl(
      `${this.$service.hrdc}/v1/en/console/rolePermission`
    );
    this.apiPermission.setParams({
      internalRoleSlugs: JSON.stringify(internalRoleSlugs),
    });
    this.apiPermission.setCallbackCompleted((response) => {
      this.$store.commit("updateRolePermission", response);
    });

    this.apiPermission.fetch();

    // Start live clock
    this.tickClock();
    this.clockInterval = setInterval(this.tickClock, 1000);

    // Start buddy image rotation every 10s
    this.buddyInterval = setInterval(() => {
      this.currentBuddyIndex = (this.currentBuddyIndex + 1) % this.buddyImages.length;
    }, 10000);

    // Fetch weather
    this.fetchWeather();
  },
  beforeDestroy() {
    if (this.clockInterval) {
      clearInterval(this.clockInterval);
    }
    if (this.buddyInterval) {
      clearInterval(this.buddyInterval);
    }
  },
  methods: {
    nextBuddy() {
      this.currentBuddyIndex = (this.currentBuddyIndex + 1) % this.buddyImages.length;
      // Show random speech bubble
      this.buddySpeech = null;
      clearTimeout(this.buddySpeechTimeout);
      this.$nextTick(() => {
        this.buddySpeech = this.buddyDialogs[Math.floor(Math.random() * this.buddyDialogs.length)];
        this.buddySpeechTimeout = setTimeout(() => {
          this.buddySpeech = null;
        }, 2000);
      });
    },
    tickClock() {
      const now = new Date();
      let h = now.getHours();
      const period = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      this.clockHours = String(h).padStart(2, '0');
      this.clockMinutes = String(now.getMinutes()).padStart(2, '0');
      this.clockSeconds = String(now.getSeconds()).padStart(2, '0');
      this.clockPeriod = period;
      this.clockBlink = !this.clockBlink;
    },
    fetchWeather() {
      // Default to Kuala Lumpur coordinates
      const lat = 3.139;
      const lon = 101.6869;

      const getWeather = (latitude, longitude, fallbackCity) => {
        fetch(`https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current_weather=true`)
          .then(res => res.json())
          .then(data => {
            if (data && data.current_weather) {
              this.weather.temp = Math.round(data.current_weather.temperature);
              this.weather.desc = this.weatherCodeToDesc(data.current_weather.weathercode);
            }
          })
          .catch(() => {
            this.weather.temp = '--';
            this.weather.desc = 'Unavailable';
          });

        // Reverse geocode to get city name
        if (fallbackCity) {
          this.weather.location = fallbackCity;
        } else {
          fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&zoom=14`, {
              headers: { 'Accept-Language': 'en' }
            })
            .then(res => res.json())
            .then(data => {
              if (data && data.address) {
                const place = data.address.town || data.address.city || data.address.suburb || data.address.village || data.address.county || '';
                const state = data.address.state || '';
                this.weather.location = place && state && place !== state ? `${place}, ${state}` : place || state;
              }
            })
            .catch(() => {
              this.weather.location = false;
            });
        }
      };

      // Try browser geolocation, fallback to KL
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => getWeather(pos.coords.latitude, pos.coords.longitude, null),
          () => getWeather(lat, lon, 'Kuala Lumpur'),
          { timeout: 5000 }
        );
      } else {
        getWeather(lat, lon, 'Kuala Lumpur');
      }
    },
    weatherCodeToDesc(code) {
      const map = {
        0: 'Clear Sky',
        1: 'Mainly Clear', 2: 'Partly Cloudy', 3: 'Overcast',
        45: 'Foggy', 48: 'Fog',
        51: 'Light Drizzle', 53: 'Drizzle', 55: 'Heavy Drizzle',
        61: 'Light Rain', 63: 'Rain', 65: 'Heavy Rain',
        71: 'Light Snow', 73: 'Snow', 75: 'Heavy Snow',
        80: 'Rain Showers', 81: 'Rain Showers', 82: 'Heavy Showers',
        95: 'Thunderstorm', 96: 'Thunderstorm', 99: 'Thunderstorm',
      };
      return map[code] || 'Partly Cloudy';
    },
    getServiceAccent(index, key) {
      // Check dynamic event module config for accent color
      const { getEventConfig } = require("@/services/hrdc_event/eventRegistry");
      const config = getEventConfig(key);
      const accent =
        config && config.accentColor
          ? config.accentColor
          : this.accentColors[index % this.accentColors.length];

      // Accents are chosen against the white dashboard, where a deep hue looks
      // right; on charcoal the darker ones fall below 3:1 (Sales Tax's #1e3a5f
      // lands at 1.05:1 — an invisible icon on an invisible tile). darkAccent()
      // keeps the hue and lifts only what needs lifting, so bright modules are
      // untouched and the cards stay individually recognisable.
      return this.isDark ? darkAccent(accent) : accent;
    },
    getServiceIcon(key) {
      if (key === "hrdc_event_settings") return "mdi-cog-outline";
      // Check event module config first (API-driven, takes priority)
      const { getEventConfig } = require("@/services/hrdc_event/eventRegistry");
      const config = getEventConfig(key);
      if (config && config.icon) return config.icon;
      // Fallback to static map
      return this.serviceIconMap[key] || 'mdi-cube-outline';
    },
  },
};
</script>

<style scoped>
/* ============================================================================
   DARK MODE
   ----------------------------------------------------------------------------
   Hand-authored — do NOT regenerate this from the light rules below. The light
   theme separates surfaces the way a light theme has to: white cards on a grey
   page, edged with a near-white hairline (rgba(226,232,240,…)). Mirroring that
   structure onto a near-black page is what made the dashboard look traced in
   white chalk — the hairline became the loudest thing on screen and the cards
   read as empty outlines rather than as raised surfaces.

   Dark inverts the technique:
     · separation comes from the surface ramp (page → card → elevated), so the
       card is already visibly lifted before any edge is drawn;
     · the edge is the palette's own blue-grey border, sitting just above the
       card rather than far above it — except on the service cards, which are
       frosted glass now and take the glass rim (a low-alpha white highlight,
       --app-card-glass-border) for the same reason the navbar does: a border
       brighter than a dark pane draws the eye to the outline, not the contents;
     · depth comes from shadow plus a 1px inner top highlight;
     · the accent stays the page's only saturated colour — the greeting hero
       keeps an accent wash instead of collapsing to charcoal, and each module
       tile keeps its own hue so the grid isn't a wall of identical grey boxes.

   Colours are token references only (--app-*, defined in
   src/plugins/theme-dark.css). No literal hexes here.
   ============================================================================ */

.dark-mode .dashboard-page {
  background-color: var(--app-page);
  /* Two faint accent glows instead of the light theme's sky/violet/emerald
     trio — three hues at this alpha turn to mud on charcoal. The recipe is
     shared with the other full-page views; it lives in theme-dark.css. */
  background-image: var(--app-page-glow);
}

/* ===== Header ===== */
.dark-mode .dashboard-header__title {
  color: var(--app-text-muted);
}

.dark-mode .dashboard-header__name {
  color: var(--app-text);
}

.dark-mode .dashboard-header__subtitle {
  color: var(--app-text-faint);
}

/* ===== Greeting hero =====
   In light mode this is the one saturated surface on the page. Flattening it
   to charcoal removed the focal point and left the mascot floating on a slab,
   so dark keeps it a hero: an accent wash over the card surface, edged with
   the accent border rather than white. */
.dark-mode .greeting-banner {
  background:
    linear-gradient(
      135deg,
      rgba(var(--app-accent-rgb), 0.18) 0%,
      rgba(var(--app-accent-rgb), 0.07) 46%,
      rgba(var(--app-accent-rgb), 0.02) 100%
    ),
    var(--app-card);
  border-color: var(--app-accent-border);
  box-shadow:
    inset 0 1px 0 var(--app-inner-highlight),
    0 10px 30px var(--app-shadow);
}

.dark-mode .greeting-banner__glow {
  background: radial-gradient(circle, rgba(var(--app-accent-rgb), 0.18) 0%, transparent 70%);
}

.dark-mode .greeting-banner__text {
  color: var(--app-text);
  text-shadow: none;
}

.dark-mode .greeting-banner__tip {
  color: var(--app-text-muted);
}

.dark-mode .buddy-bubble {
  background: var(--app-elevated);
  color: var(--app-text);
  box-shadow: 0 4px 14px var(--app-shadow);
}

.dark-mode .buddy-bubble::after {
  border-top-color: var(--app-elevated);
}

/* ===== Stats bar ===== */
.dark-mode .stats-bar {
  background: var(--app-card);
  border-color: var(--app-border);
  box-shadow:
    inset 0 1px 0 var(--app-inner-highlight),
    0 6px 20px var(--app-shadow-mid);
}

/* The three section markers are the bar's only colour; slate goes to the
   border tone so it reads as a divider rather than a fourth status hue. */
.dark-mode .stats-bar__section--weather {
  border-left-color: var(--app-warning);
}

.dark-mode .stats-bar__section--date {
  border-left-color: var(--app-border-strong);
}

.dark-mode .stats-bar__section--clock {
  border-left-color: var(--app-accent);
}

.dark-mode .stats-bar__icon-circle--amber {
  background: var(--app-warning-bg);
}

.dark-mode .stats-bar__icon-circle--slate,
.dark-mode .stats-bar__icon-circle--gray {
  background: var(--app-elevated);
}

.dark-mode .stats-bar__icon-circle--blue {
  background: var(--app-accent-soft);
}

.dark-mode .stats-bar__value {
  color: var(--app-text);
}

.dark-mode .stats-bar__label {
  color: var(--app-text-muted);
}

.dark-mode .stats-bar__location {
  color: var(--app-text-faint);
}

.dark-mode .clock__digit,
.dark-mode .clock__colon {
  color: var(--app-text);
}

.dark-mode .clock__period {
  color: var(--app-text-faint);
}

/* ===== Zone headers ===== */
.dark-mode .service-zone__title {
  color: var(--app-text-muted);
}

.dark-mode .service-zone__subtitle {
  color: var(--app-text-faint);
}

/* A full-width hairline is the same mistake as a bordered card; fading it out
   keeps the heading anchored without ruling a line across the page. */
.dark-mode .service-zone__rule {
  background: linear-gradient(90deg, var(--app-border) 0%, transparent 100%);
}

.dark-mode .running-events-pill {
  background: var(--app-accent-soft);
  border-color: var(--app-accent-border);
  color: var(--app-accent-strong);
}

.dark-mode .running-events-pill:hover {
  background: rgba(var(--app-accent-rgb), 0.22);
  border-color: var(--app-accent);
  box-shadow: 0 4px 14px var(--app-shadow);
}

.dark-mode .running-events-pill .v-icon {
  color: var(--app-accent-strong) !important;
}

.dark-mode .running-events-pill__dot {
  background: var(--app-accent);
  box-shadow: 0 0 0 3px var(--app-accent-soft);
}

/* Neutral by design: this sits beside the accent-coloured "N Active" pill, and
   a second saturated hue there competes for the same attention. The surface ramp gives
   it presence instead. */
.dark-mode .event-settings-pill {
  background: var(--app-elevated);
  border-color: var(--app-border-strong);
  color: var(--app-text-muted);
}

.dark-mode .event-settings-pill:hover {
  background: var(--app-elevated);
  border-color: var(--app-text-faint);
  color: var(--app-text);
  box-shadow: 0 4px 14px var(--app-shadow);
}

.dark-mode .event-settings-pill .v-icon {
  color: var(--app-text-muted) !important;
}

.dark-mode .event-settings-pill:hover .v-icon {
  color: var(--app-text) !important;
}

/* ===== Service cards =====
   Everything about the card's MATERIAL — the frosted fill, the rim, the sheen,
   the lift — is token-driven now (--app-card-glass*, --app-glass-sheen), so the
   whole light/dark split for it happens in theme-dark.css and there is no
   companion rule to keep in step here.

   What is left is the one thing tokens can't carry: the module's own hue is
   drawn from currentColor at a fixed ALPHA on the icon tile, and an alpha tuned
   against a near-white pane barely registers on a charcoal one. The tile's wash
   and ring come up a step, which is also what keeps a dark grid from collapsing
   into a wall of identical grey panes. */
.dark-mode .service-card--disabled {
  opacity: 0.42;
}

/* Dark's sheen token is a tenth of light's to begin with; anything taken off
   it here and the face of the glass goes unlit. */
.dark-mode .service-card::before {
  opacity: 1;
}

.dark-mode .service-card__icon::before {
  opacity: 0.16;
}

.dark-mode .service-card__icon::after {
  opacity: 0.3;
}

.dark-mode .service-card:hover .service-card__icon::before {
  opacity: 0.26;
}

.dark-mode .service-card:hover .service-card__icon::after {
  opacity: 0.52;
}

.dark-mode .service-card__name {
  color: var(--app-text);
}

.dark-mode .service-card__desc {
  color: var(--app-text-muted);
}

.dark-mode .service-card__status--disabled {
  background: var(--app-elevated);
  color: var(--app-text-faint);
}

.dark-mode .service-card__status--restricted {
  background: var(--app-danger-bg);
  color: var(--app-danger-fg);
}


.dark-mode .service-card__go {
  color: var(--app-text-faint) !important;
}

.dark-mode .service-card:hover .service-card__go {
  color: var(--app-accent) !important;
}

/* ===== Skeleton ===== */
.dark-mode .dskel-line,
.dark-mode .dskel-circle,
.dark-mode .dskel-banner,
.dark-mode .dskel-card__accent,
.dark-mode .dskel-card__icon,
.dark-mode .skel-text--temp,
.dark-mode .skel-text--desc,
.dark-mode .stats-bar__location-skel .skel-text {
  background: linear-gradient(
    90deg,
    var(--app-elevated) 25%,
    var(--app-border-strong) 50%,
    var(--app-elevated) 75%
  );
  background-size: 200% 100%;
}

.dark-mode .dskel-stats {
  background: var(--app-card);
  border: 1px solid var(--app-border);
  box-shadow:
    inset 0 1px 0 var(--app-inner-highlight),
    0 4px 14px var(--app-shadow-mid);
}

.dark-mode .dskel-stats__divider {
  background: var(--app-border);
}

@media (max-width: 599px) {
  .dark-mode .stats-bar__section {
    border-bottom-color: var(--app-border);
  }

  .dark-mode .stats-bar__section--weather {
    border-bottom-color: var(--app-warning) !important;
  }

  .dark-mode .stats-bar__section--date {
    border-bottom-color: var(--app-border-strong) !important;
  }

  .dark-mode .stats-bar__section--clock {
    border-bottom-color: var(--app-accent) !important;
  }
}

/* ===== end DARK MODE ===== */

.dashboard-page {
  background-color: #d8dce2;
  background-image:
    radial-gradient(circle at 15% 20%, rgba(14, 165, 233, 0.06) 0%, transparent 50%),
    radial-gradient(circle at 85% 60%, rgba(139, 92, 246, 0.05) 0%, transparent 45%),
    radial-gradient(circle at 50% 90%, rgba(16, 185, 129, 0.04) 0%, transparent 40%);
  /* Fill whatever main has left after the breadcrumb and footer. A `100vh -
     64px` guess only subtracts the app bar, so it overshot by the height of
     both and forced a scrollbar on every dashboard visit. */
  flex: 1 0 auto;
  padding: 36px 44px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ===== Header ===== */
.dashboard-header {
  margin-bottom: 32px;
}

.dashboard-header__title {
  font-size: 1.5rem;
  font-weight: 400;
  color: #475569;
  margin: 0 0 6px;
}

.dashboard-header__name {
  font-weight: 800;
  color: #0f172a;
}

.dashboard-header__subtitle {
  font-size: 0.9rem;
  color: #94a3b8;
  margin: 0;
  font-weight: 400;
}

/* ===== Banner Row ===== */
.banner-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-bottom: 28px;
}

/* ===== Greeting Banner ===== */
.greeting-banner {
  background: linear-gradient(135deg, #0ea5e9 0%, #38bdf8 50%, #7dd3fc 100%);
  border-radius: 16px;
  padding: 28px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow:
    0 4px 24px rgba(14, 165, 233, 0.25),
    0 1px 4px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.greeting-banner__glow {
  position: absolute;
  top: -40%;
  right: -10%;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.12) 0%, transparent 70%);
  pointer-events: none;
}

.greeting-banner__left {
  display: flex;
  align-items: center;
  gap: 18px;
  position: relative;
  z-index: 1;
}

.greeting-banner__emoji {
  font-size: 3.2rem;
  line-height: 1;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.greeting-banner__text {
  font-size: 1.3rem;
  font-weight: 800;
  color: #ffffff;
  margin: 0 0 6px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.greeting-banner__tip {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  font-weight: 400;
  font-style: italic;
  max-width: 380px;
  line-height: 1.5;
}

/* Mascot */
.greeting-banner__mascot {
  flex-shrink: 0;
  width: 100px;
  height: 100px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
}

.greeting-banner__mascot-img {
  max-width: 140%;
  max-height: 140%;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15));
}

/* Mascot fade transition */
/* Speech Bubble */
.buddy-bubble {
  position: absolute;
  top: -10px;
  right: -8px;
  background: #fff;
  color: #1e293b;
  font-family: 'Lato', sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 5px 12px;
  border-radius: 12px;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.12);
  white-space: nowrap;
  z-index: 3;
  pointer-events: none;
  transform: rotate(-8deg);
}

.buddy-bubble::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #fff;
}

/* Bubble pop transition */
.bubble-pop-enter-active {
  animation: bubblePop 0.3s ease-out;
}

.bubble-pop-leave-active {
  animation: bubbleFade 0.3s ease-in;
}

@keyframes bubblePop {
  0% { opacity: 0; transform: scale(0.5) translateY(8px); }
  60% { transform: scale(1.1) translateY(-2px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}

@keyframes bubbleFade {
  0% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0.8) translateY(-4px); }
}

.mascot-fade-enter-active,
.mascot-fade-leave-active {
  transition: opacity 0.6s ease;
}

.mascot-fade-enter,
.mascot-fade-leave-to {
  opacity: 0;
}

/* ===== Stats Bar ===== */
/* Same correction as .service-card: on the frosted sheet a white panel needs a
   full-strength rim and the shared lift, or its edges disappear. */
.stats-bar {
  background: var(--app-card);
  border: 1px solid var(--app-border);
  border-radius: 14px;
  box-shadow: var(--glass-lift);
  padding: 16px 28px;
  display: flex;
  align-items: center;
  gap: 24px;
}

.stats-bar__section {
  display: flex;
  align-items: center;
}

.stats-bar__section--weather {
  padding-left: 12px;
  border-left: 3px solid #f59e0b;
}

.stats-bar__section--date {
  padding-left: 12px;
  border-left: 3px solid #64748b;
}

.stats-bar__section--clock {
  padding-left: 12px;
  border-left: 3px solid #0ea5e9;
}

.stats-bar__item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.stats-bar__icon-circle {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stats-bar__icon-circle--amber {
  background: #fef3c7;
}

.stats-bar__icon-circle--slate {
  background: #f1f5f9;
}

.stats-bar__icon-circle--blue {
  background: #e0f2fe;
}

.stats-bar__icon-circle--gray {
  background: #f1f5f9;
}

.stats-bar__value {
  font-size: 0.92rem;
  font-weight: 700;
  color: #1e293b;
}

.stats-bar__label {
  font-size: 0.78rem;
  color: #94a3b8;
  font-weight: 500;
}

.stats-bar__location {
  font-size: 0.72rem;
  color: #94a3b8;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: 4px;
}

.stats-bar__location-skel {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
}

.stats-bar__location-skel .skel-text {
  display: inline-block;
  width: 80px;
  height: 12px;
  border-radius: 4px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: skel-shimmer 1.5s infinite ease-in-out;
}

@keyframes skel-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}



/* ===== Live Clock ===== */
.clock {
  display: inline-flex;
  align-items: baseline;
  font-family: 'Lato', monospace;
}

.clock__digit {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
  min-width: 1.1em;
  text-align: center;
}

.clock__colon {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 1px;
}

.clock__colon--blink {
  opacity: 0.3;
}

.clock__period {
  font-size: 0.65rem;
  font-weight: 600;
  color: #94a3b8;
  margin-left: 4px;
}

/* ===== Zone ===== */
.service-zone {
  margin-bottom: 32px;
}

.service-zone:last-child {
  margin-bottom: 0;
}

.service-zone__header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 18px;
}

.service-zone__heading {
  flex-shrink: 0;
}

.service-zone__title {
  font-size: 0.78rem;
  font-weight: 800;
  color: #475569;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1.2px;
}

.service-zone__subtitle {
  font-size: 0.74rem;
  color: #94a3b8;
  margin: 3px 0 0;
  font-weight: 400;
}

.service-zone__rule {
  flex: 1;
  height: 1px;
  background: rgba(148, 163, 184, 0.35);
}

.running-events-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 999px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  color: #0369a1;
  font-size: 0.76rem;
  font-weight: 700;
  white-space: nowrap;
  transition: all 0.2s ease;
}
.running-events-pill:hover {
  background: #e0f2fe;
  border-color: #7dd3fc;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(2, 132, 199, 0.15);
}
.running-events-pill__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.2);
  animation: running-events-pulse 2s ease-in-out infinite;
}
.running-events-pill__label {
  letter-spacing: 0.2px;
}
@keyframes running-events-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.event-settings-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  color: #475569;
  font-size: 0.76rem;
  font-weight: 700;
  white-space: nowrap;
  text-decoration: none;
  transition: all 0.2s ease;
}
.event-settings-pill:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
  color: #1e293b;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(71, 85, 105, 0.15);
}
.event-settings-pill__label {
  letter-spacing: 0.2px;
}

/* ===== Grid ===== */
.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 18px;
  align-items: stretch;
}

.service-hidden {
  display: none;
}

.service-card-link {
  text-decoration: none;
  color: inherit;
  display: flex;
  height: 100%;
}

/* ===== Card — FLOATING GLASS =====
   These are the one place in the app where a CONTENT card is frosted, and the
   condition theme-glass.css sets for it is met here: the surface behind them is
   not the flat ground but the frosted page sheet, which is itself translucent
   over the console's ground glow. So there is something worth blurring, and the
   tint each card picks up from the glow underneath is what makes the grid read
   as panes of one sheet of glass instead of a row of identical boxes.

   Three layers, in paint order:
     backdrop  blur + saturate — blur alone desaturates whatever shows through,
               so the sheet would go grey under every card without the saturate
     fill      --app-card-glass, thin enough to see the page through — it runs
               at the CHROME's alpha tier, not a painted surface's. What keeps
               it a step above the sheet at that thinness is the brightness()
               in the backdrop filter, not more white; see the @supports block
     ::before  the sheen — light on the top of the face, cut to the top 55% so
               it has faded out well before the label row
   The module's own hue stays inside the icon tile — a bloom washing it across
   the pane read as colour splashing around the card. The sheen sits at
   z-index 0 and .service-card__content at 1, so no label is ever reading
   through a gradient. `isolation` keeps that stack private to the card rather
   than letting it resolve against the page.

   COLOUR IS ENTIRELY IN TOKENS (--app-card-glass*, --app-glass-sheen, per
   theme in theme-dark.css), so none of the material below needs a dark
   companion rule. The only thing the dark block still carries for these cards
   is the ALPHA on the accent layers, which a token cannot flip — see the note
   there. */
.service-card {
  /* Opaque baseline; the frosted version is in the @supports block. */
  background: var(--app-card);
  border: 1px solid var(--app-card-glass-border);
  border-radius: var(--glass-radius);
  /* --glass-lift's tight first pass is 0.04 ink, which is enough under a card
     that also carries an ink rim. This one's rim is white light, so the contact
     shadow IS the separation from the sheet — same two-pass shape, one step
     firmer on the near pass. */
  box-shadow:
    0 1px 3px var(--app-shadow-mid),
    0 10px 30px var(--app-shadow-mid),
    inset 0 1px 0 var(--app-inner-highlight);
  display: flex;
  overflow: hidden;
  width: 100%;
  height: 100%;
  transition:
    transform 0.28s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.28s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.28s ease,
    border-color 0.28s ease;
  cursor: pointer;
  position: relative;
  isolation: isolate;
}

/* The sheen — see the block above. `border-radius: inherit` matters: without
   it the gradient squares off the two top corners the card just rounded. */
.service-card::before {
  content: '';
  position: absolute;
  inset: 0 0 auto;
  height: 55%;
  border-radius: inherit;
  background: var(--app-glass-sheen);
  /* The token is cut for the navbar, which is a fraction of a card's height —
     at full strength across 55% of a 130px pane the top half reads as washed
     out rather than lit. Dark's sheen is already only 0.1 white and takes none
     of this off (see the dark block). */
  opacity: 0.8;
  pointer-events: none;
  z-index: 0;
}

/* Hover lifts the pane and brightens its rim — the fill moves too, but only
   within the glass tier (--app-card-glass-hover), never toward --app-elevated,
   which would land back on the sheet's own value. */
.service-card:hover {
  box-shadow:
    var(--glass-lift-hover),
    inset 0 1px 0 var(--app-inner-highlight);
  border-color: var(--app-card-glass-border-hover);
  transform: translateY(-4px);
}

.service-card--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.service-card--disabled:hover {
  box-shadow:
    0 1px 3px var(--app-shadow-mid),
    0 10px 30px var(--app-shadow-mid),
    inset 0 1px 0 var(--app-inner-highlight);
  border-color: var(--app-card-glass-border);
  transform: none;
}

/* The frost itself — the same two-step theme-glass.css uses for every frosted
   surface. Where backdrop-filter is missing, the rules above have already left
   the card on an opaque fill; a translucent one with nothing blurred behind it
   would put the sheet's own edges through the labels. */
@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .service-card {
    background: var(--app-card-glass);
    /* THREE functions, and the card needs all three at this alpha:
         blur        --glass-blur-panel rather than --glass-blur; the thinner the
                     fill, the more the radius is what keeps the ground glow
                     behind the pane reading as light rather than as a shape
         saturate    blur on its own desaturates whatever shows through, which
                     would drain the glow's tint out of every card and put the
                     grid back to uniform grey
         brightness  the one that makes a 0.38 fill work at all — it lifts the
                     backdrop BEFORE the fill composites over it, so the card
                     still lands a step above the sheet instead of within a
                     couple of levels of it. Same token, opposite job per theme:
                     light pushes the sheet toward white, dark lifts a near-black
                     backdrop so the pane is not a hole. */
    -webkit-backdrop-filter:
      blur(var(--glass-blur-panel))
      saturate(var(--glass-saturate))
      brightness(var(--app-glass-brightness));
    backdrop-filter:
      blur(var(--glass-blur-panel))
      saturate(var(--glass-saturate))
      brightness(var(--app-glass-brightness));
  }

  .service-card:hover {
    background: var(--app-card-glass-hover);
  }

  .service-card--disabled:hover {
    background: var(--app-card-glass);
  }
}


/* ===== Card Content ===== */
.service-card__content {
  flex: 1;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  /* Above the sheen and the bloom. */
  position: relative;
  z-index: 1;
}

/* ===== Top Row ===== */
.service-card__top {
  display: flex;
  /* The card no longer carries a description line, so the name is the whole
     text column — centring it against the icon tile is what keeps the two
     reading as one row instead of a label pinned to the tile's top edge. */
  align-items: center;
  gap: 14px;
}

/* The tile is built from currentColor — which the template sets to the module's
   accent — instead of from an inline `accent + '14'` fill. That literal was
   tuned against a white card and all but vanished on a dark one, so dark mode
   had to blank it with `background: transparent !important` and rebuild it
   anyway; now BOTH themes rebuild it the same way and only the alphas differ.
   Two pseudos, not one: `opacity` applies to a whole pseudo-element, so a ring
   drawn on the wash would be diluted to the wash's alpha and disappear. */
.service-card__icon {
  position: relative;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition:
    transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.25s ease;
}

.service-card__icon::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: currentColor;
  opacity: 0.12;
  transition: opacity 0.25s ease;
}

.service-card__icon::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  box-shadow: inset 0 0 0 1px currentColor;
  opacity: 0.22;
  transition: opacity 0.25s ease;
}

.service-card__icon .v-icon {
  position: relative;
}

/* Hovering the card lights the module's colour rather than only scaling the
   tile: the wash and ring come up, and the tile drops a short glow of its own
   hue. The negative spread keeps that glow under the tile instead of leaking a
   coloured halo across the glass. */
.service-card:hover .service-card__icon {
  transform: scale(1.06) translateY(-1px);
  box-shadow: 0 10px 20px -10px currentColor;
}

.service-card:hover .service-card__icon::before {
  opacity: 0.2;
}

.service-card:hover .service-card__icon::after {
  opacity: 0.4;
}

.service-card__info {
  flex: 1;
  min-width: 0;
}

.service-card__name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
  /* The 4px gutter belonged to the description underneath; with that gone it
     just nudges the name off the tile's centre line. */
  margin: 0;
  line-height: 1.35;
}

.service-card__desc {
  font-size: 0.78rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* ===== Bottom Row ===== */
.service-card__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 16px;
}

.service-card__status {
  font-size: 0.68rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding: 4px 10px;
  border-radius: 20px;
}

.service-card__status--disabled {
  color: #94a3b8;
  background: #f8fafc;
}

.service-card__status--restricted {
  color: #dc2626;
  background: #fef2f2;
}


/* Go arrow.
   `space-between` on the row put the arrow hard left as soon as it lost the
   pill it used to be spaced against, so the alignment is pinned here instead
   of inferred from having a sibling. */
.service-card__go {
  margin-left: auto;
  color: #cbd5e1 !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.service-card:hover .service-card__go {
  color: #0ea5e9 !important;
  transform: translateX(4px);
}

/* ===== MOBILE (< 600px) ===== */
@media (max-width: 599px) {
  .dashboard-page {
    padding: 16px 14px !important;
  }

  /* Header */
  .dashboard-header {
    margin-bottom: 20px;
  }

  .dashboard-header__title {
    font-size: 1.15rem;
  }

  .dashboard-header__subtitle {
    font-size: 0.82rem;
  }

  /* Greeting Banner */
  .greeting-banner {
    padding: 20px 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .greeting-banner__mascot {
    display: none;
  }

  .greeting-banner__emoji {
    font-size: 2.2rem;
  }

  .greeting-banner__text {
    font-size: 1.05rem;
  }

  .greeting-banner__tip {
    font-size: 0.78rem;
  }

  /* Banner Row */
  .banner-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .stats-bar {
    flex-direction: column;
    padding: 14px 18px;
    gap: 14px;
  }

  .stats-bar__section {
    width: 100%;
    border-left: none !important;
    padding-left: 0 !important;
    padding-bottom: 14px;
    border-bottom: 3px solid #e2e8f0;
  }

  .stats-bar__section:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .stats-bar__section--weather {
    border-bottom-color: #f59e0b !important;
  }

  .stats-bar__section--date {
    border-bottom-color: #64748b !important;
  }

  .stats-bar__section--clock {
    border-bottom-color: #0ea5e9 !important;
  }

  /* Zones */
  .service-zone {
    margin-bottom: 24px;
  }

  .service-zone__subtitle {
    font-size: 0.7rem;
  }

  /* Zone header: heading on its own row, pills share the row below */
  .service-zone__header {
    gap: 10px;
    margin-bottom: 14px;
  }

  .service-zone__heading {
    flex: 1 1 100%;
    min-width: 0;
  }

  .service-zone__rule {
    display: none;
  }

  /* Zone actions: two equal-width buttons that share the row, and
     stack to full width when the viewport is too narrow for both. */
  .running-events-pill,
  .event-settings-pill {
    flex: 1 1 140px;
    min-width: 0;
    justify-content: center;
    gap: 7px;
    padding: 11px 12px;
    border-radius: 12px;
    font-size: 0.74rem;
    line-height: 1.2;
  }

  .running-events-pill:hover,
  .event-settings-pill:hover {
    transform: none;
  }

  .running-events-pill__label,
  .event-settings-pill__label {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Chevrons read as clutter once the labels are centred, and the
     calendar icon is redundant beside the live dot — dropping both
     leaves each button with one leading glyph, so they match. */
  .zone-pill__chevron,
  .running-events-pill__icon {
    display: none;
  }

  /* Service Grid */
  .services-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .service-card__content {
    padding: 16px 16px;
    gap: 12px;
  }

  .service-card__icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
  }

  .service-card__name {
    font-size: 0.88rem;
  }

  .service-card__desc {
    font-size: 0.72rem;
  }

  .service-card__status {
    font-size: 0.63rem;
  }

  /* Clock */
  .clock__digit,
  .clock__colon {
    font-size: 0.85rem;
  }

  /* Skeleton */
  .dskel-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .dskel-line--xl {
    width: 100%;
    max-width: 260px;
  }

  .dskel-stats {
    flex-wrap: wrap;
    padding: 14px 18px;
  }
}

/* ===== TABLET (600px - 959px) ===== */
@media (min-width: 600px) and (max-width: 959px) {
  .dashboard-page {
    padding: 24px 24px !important;
  }

  .dashboard-header {
    margin-bottom: 24px;
  }

  /* Greeting Banner */
  .greeting-banner__mascot {
    display: none;
  }

  /* Stats Bar */
  .stats-bar {
    flex-wrap: wrap;
    gap: 14px;
    padding: 14px 22px;
  }

  /* Service Grid */
  .services-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 14px;
  }

  .dskel-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .dskel-banner {
    height: 80px;
    border-radius: 14px;
  }

  .dskel-stats {
    padding: 14px 16px;
    border-radius: 12px;
  }
}

/* ============================================
   DASHBOARD SKELETON LOADER
   ============================================ */
.dashboard-skeleton {
  font-family: 'Lato', sans-serif;
}

@keyframes dskel-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.dskel-line {
  height: 14px;
  border-radius: 7px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: dskel-shimmer 1.5s infinite ease-in-out;
}

.dskel-line--xl { width: 260px; height: 22px; margin-bottom: 10px; }
.dskel-line--lg { width: 200px; }
.dskel-line--md { width: 140px; }
.dskel-line--sm { width: 90px; }
.dskel-line--xs { width: 60px; }

.dskel-circle {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: dskel-shimmer 1.5s infinite ease-in-out;
  flex-shrink: 0;
}

/* Header skeleton */
.dskel-header {
  margin-bottom: 20px;
}

/* Banner skeleton */
.dskel-banner {
  height: 100px;
  border-radius: 16px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: dskel-shimmer 1.5s infinite ease-in-out;
  margin-bottom: 20px;
}

/* Stats bar skeleton */
.dskel-stats {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  margin-bottom: 24px;
}

.dskel-stats__item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dskel-stats__divider {
  width: 1px;
  height: 24px;
  background: #e8eaed;
  flex-shrink: 0;
}

/* Service cards skeleton */
.dskel-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

/* The placeholder stands in for a glass card, so it is one — same fill, rim,
   radius and lift, or the grid visibly re-materialises when the API lands. */
.dskel-card {
  display: flex;
  /* Frosted like the card it stands in for — at this alpha the fill alone is
     too thin to read as a surface without the backdrop work behind it. */
  background: var(--app-card-glass);
  -webkit-backdrop-filter:
    blur(var(--glass-blur-panel))
    saturate(var(--glass-saturate))
    brightness(var(--app-glass-brightness));
  backdrop-filter:
    blur(var(--glass-blur-panel))
    saturate(var(--glass-saturate))
    brightness(var(--app-glass-brightness));
  border: 1px solid var(--app-card-glass-border);
  border-radius: var(--glass-radius);
  box-shadow:
    0 1px 3px var(--app-shadow-mid),
    0 10px 30px var(--app-shadow-mid),
    inset 0 1px 0 var(--app-inner-highlight);
  overflow: hidden;
}

.dskel-card__accent {
  width: 4px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: dskel-shimmer 1.5s infinite ease-in-out;
  flex-shrink: 0;
}

.dskel-card__body {
  flex: 1;
  padding: 18px 20px;
}

.dskel-card__top {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 16px;
}

.dskel-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: dskel-shimmer 1.5s infinite ease-in-out;
  flex-shrink: 0;
}

.dskel-card__lines {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;
}

.dskel-card__bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Weather skeleton text */
.skel-text--temp {
  display: inline-block;
  width: 45px;
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: dskel-shimmer 1.5s infinite ease-in-out;
  margin-left: 8px;
}

.skel-text--desc {
  display: inline-block;
  width: 70px;
  height: 12px;
  border-radius: 4px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: dskel-shimmer 1.5s infinite ease-in-out;
  margin-left: 8px;
}

</style>
